import { OnlineBookingToast } from '@common/OnlineBookingToast';
import { Patient } from '@store/patientStore';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildBookingToasts } from './BookingToastCenter.utils';

export interface BookingToastCenterProps {
  currentStep: StepsStep;
  changeToastType: string;
  onDismissChangeToast: () => void;
  patientsToCheck: string[];
  onDismissPatientsToCheck: () => void;
  currentPatient?: Patient;
  currentPatientIndex: number;
  patients: Array<Patient | undefined>;
  isRescheduling?: boolean;
}

export const BookingToastCenter = ({
  currentStep,
  changeToastType,
  onDismissChangeToast,
  patientsToCheck,
  onDismissPatientsToCheck,
  currentPatient,
  currentPatientIndex,
  patients,
  isRescheduling,
}: BookingToastCenterProps) => {
  const { t } = useTranslation();
  const [dismissedToastKeys, setDismissedToastKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const toasts = useMemo(
    () =>
      buildBookingToasts({
        currentStep,
        changeToastType,
        onDismissChangeToast,
        patientsToCheck,
        onDismissPatientsToCheck,
        currentPatient,
        currentPatientIndex,
        patients,
        isRescheduling,
        t,
      }),
    [
      changeToastType,
      currentPatient,
      currentPatientIndex,
      currentStep,
      isRescheduling,
      onDismissChangeToast,
      onDismissPatientsToCheck,
      patients,
      patientsToCheck,
      t,
    ],
  );

  const activeToastKeySignature = useMemo(
    () => toasts.map(toast => toast.key).join('|'),
    [toasts],
  );

  useEffect(() => {
    const activeToastKeys = new Set(toasts.map(toast => toast.key));
    setDismissedToastKeys(prev => {
      const next = new Set(
        Array.from(prev).filter(key => activeToastKeys.has(key)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [activeToastKeySignature, toasts]);

  return (
    <>
      {toasts
        .filter(toast => !dismissedToastKeys.has(toast.key))
        .map(toast => (
          <OnlineBookingToast
            key={toast.key}
            open
            title={toast.title}
            description={toast.description}
            severity={toast.severity}
            icon={toast.icon}
            showCloseIcon={toast.showCloseIcon}
            onClose={() => {
              setDismissedToastKeys(prev => new Set(prev).add(toast.key));
              toast.onDismiss?.();
            }}
          />
        ))}
    </>
  );
};
